/** @jsxImportSource @emotion/react */
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTheme } from '../../ThemeProvider';
import { CoreButton, CoreProps } from '../Button/CoreButton';

export type Props = Omit<CoreProps, 'utility' | 'ref'> & {
  dropdown?: (({ close }: { close: () => void }) => ReactNode) | ReactNode;
};

export const UtilityButton = ({ dropdown, onClick, ...props }: Props) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { spacing } = useTheme();
  const [active, setActive] = useState<boolean>();
  const [rightPos, setRightPos] = useState<boolean>(false);

  useEffect(() => {
    if (buttonRef.current && dropdownRef.current) {
      const buttonBBox = buttonRef.current.getBoundingClientRect();
      const dropdownBBox = dropdownRef.current.getBoundingClientRect();
      if (buttonBBox.x + dropdownBBox.width > window.innerWidth) {
        setRightPos(true);
      } else {
        setRightPos(false);
      }
    }
  }, [buttonRef, dropdownRef, setRightPos, active]);
  return (
    <span
      css={{ position: 'relative' }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setActive(false);
      }}
      tabIndex={1}
    >
      <CoreButton
        ref={buttonRef}
        onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          setActive(!active);
          onClick && onClick(e);
        }}
        endIcon={dropdown ? 'chevron' : undefined}
        {...props}
        utility
        css={
          props.endIcon === undefined && {
            '.MuiButton-endIcon': {
              width: spacing.xxs,
              height: spacing.xxs,
              div: {
                width: spacing.xxs,
                height: spacing.xxs,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                svg: {
                  transform: 'rotate(-90deg)',
                },
              },
            },
          }
        }
      />
      {dropdown && active && (
        <div
          id="dropdown"
          css={{
            position: 'absolute',
            zIndex: 10,
            marginTop: spacing.xxxs,
            right: rightPos ? 0 : 'unset',
            left: rightPos ? 'unset' : 0,
          }}
          ref={dropdownRef}
        >
          {typeof dropdown === 'function' &&
            dropdown({ close: () => setActive(false) })}
          {typeof dropdown !== 'function' && dropdown}
        </div>
      )}
    </span>
  );
};
